import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, Link, Navigate } from 'react-router-dom';
import { MessageSquare, ChevronLeft, FileText, Wrench, Pencil, Check, X, Rocket, Lock } from 'lucide-react';
import { Card } from '../../components/ui/Card.jsx';
import { ensureBuilderSession, fetchAllOwnedIvrAvatars, renameAvatar, setAvatarEnabled } from '../../api/ivrAgents.js';
import { useTenant } from '../../components/TenantContext.jsx';
import { useSSEChat } from '../../hooks/useSSEChat.js';
import { useTenantDids } from '../../hooks/useTenantDids.js';
import { ChatPane } from '../../components/IvrAgents/Workspace/ChatPane.jsx';
import { SourcesPane } from '../../components/IvrAgents/Workspace/SourcesPane.jsx';
import { WorkshopPane } from '../../components/IvrAgents/Workspace/WorkshopPane.jsx';
import { LineBindModal } from '../../components/IvrAgents/Workspace/LineBindModal.jsx';
import { BuildProgressBadge } from '../../components/IvrAgents/Workspace/BuildProgressBadge.jsx';

export default function IvrAgentWorkspacePage() {
    const { t } = useTranslation('workspace');
    const { avatarId } = useParams();
    const { hostId, isAdmin, role } = useTenant();
    // 母帳號 (admin) 與被指派的「客服」子帳號 (agent) 可編輯；「檢視」子帳號 (viewer) 唯讀,只能模擬通話
    const readOnly = !isAdmin && role !== 'agent';
    const [sessionId, setSessionId] = useState(null);
    const [sessionError, setSessionError] = useState(null);
    const [avatar, setAvatar] = useState(null);
    const [accessChecked, setAccessChecked] = useState(false);
    // 試打帶的 did 會寫進 callLog.did —— 取進線路由的真值，不要用沒人維護的 avatar.did
    // （實測 15 筆 avatar.did 有 7 筆過期，會讓通話記錄的 DID 欄寫到別隻角色的號碼）
    // 跟下面送給 WorkshopPane 的 hostId 用同一個來源（角色自己的 hostId，退回租戶 context）——
    // did 與 hostId 一起進 WS 參數，兩者必須來自同一個租戶的路由表
    const didsByAvatar = useTenantDids(avatar?.raw?.hostId || avatar?.hostId || hostId);

    useEffect(() => {
        if (readOnly) return;   // 唯讀子帳號不開 builder session(沒有聊天編輯)
        let cancelled = false;
        ensureBuilderSession(avatarId)
            .then((r) => { if (!cancelled) setSessionId(r.sessionId); })
            .catch((e) => {
                if (!cancelled) setSessionError(e);
                console.error('[Workspace] ensureBuilderSession failed:', e);
            });
        return () => { cancelled = true; };
    }, [avatarId, readOnly]);

    // 整個 workspace 共用同一個 SSE chat — 左右欄也能呼 sendMessage
    const chat = useSSEChat(sessionId);

    const [publishError, setPublishError] = useState(null);
    const [lineBindOpen, setLineBindOpen] = useState(false);

    // 「發佈」= 上線這個角色：先把 enabled 設 true(webcall 才會拿掉「僅供預覽」浮水印、
    // 電話端才可綁 DID),再送 sync-ivr-config skill 同步 flow config + 清 Redis cache。
    async function handlePublish() {
        setPublishError(null);
        try {
            await setAvatarEnabled(avatarId, true);
            setAvatar((a) => (a ? { ...a, enabled: true } : a));
        } catch (e) {
            // 商用帳號啟用數超額 → 後端回 409 role_limit_exceeded,擋下並提示,不繼續 sync
            const msg = e?.body?.message || e?.message || t('publish.enableFailed');
            setPublishError(msg);
            return;
        }
        chat.sendMessage(t('publish.prompt'), { displayText: t('publish.displayText') });
    }

    // avatar 載入 — 走 tenant scope(子帳號只拿得到被指派的 → 同時當權限把關)
    // 更新後的重抓帶 refresh=true(admin 編輯後);tenant-avatars 本來就 fresh,雙保險
    useEffect(() => {
        if (!hostId) return;
        let cancelled = false;
        fetchAllOwnedIvrAvatars({ hostId, refresh: !!chat.lastCompletedAt })
            .then((list) => {
                if (cancelled) return;
                setAvatar(list.find((a) => a.avatarId === avatarId) || null);
                setAccessChecked(true);
            })
            .catch(() => { if (!cancelled) setAccessChecked(true); });
        return () => { cancelled = true; };
    }, [avatarId, hostId, chat.lastCompletedAt]);

    // 子帳號開到「沒被指派」的 avatar → 不在 scoped list → 導回列表
    if (readOnly && accessChecked && !avatar) {
        return <Navigate to="/ivr-agents" replace />;
    }

    return (
        <div className="-mx-10 -my-8 px-3 py-3 h-[calc(100vh-5rem)] flex flex-col gap-2">
            <div className="flex items-center justify-between px-1">
                <div className="flex items-center gap-3">
                    <Link
                        to="/ivr-agents"
                        className="inline-flex items-center gap-1 text-sm text-ink-500 hover:text-accent-700 transition"
                    >
                        <ChevronLeft size={16} />
                        {t('header.backToList')}
                    </Link>
                    {avatar?.name && (readOnly ? (
                        <span className="heading-display text-base font-semibold text-ink-900">{avatar.name}</span>
                    ) : (
                        <AvatarNameEditor
                            name={avatar.name}
                            onRename={async (newName) => {
                                await renameAvatar(avatarId, newName);
                                setAvatar((a) => a ? { ...a, name: newName } : a);
                            }}
                        />
                    ))}
                    {readOnly && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-cream-100 text-ink-500 text-[10px] font-mono uppercase tracking-wider">
                            <Lock size={10} /> {t('header.readOnly')}
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-3">
                    <div className="hidden sm:flex flex-col items-end leading-none gap-1">
                        <code className="font-mono text-[10px] tracking-tight text-ink-400">
                            <span className="text-ink-300">id · </span>{avatarId}
                        </code>
                        {sessionId && (
                            <code className="font-mono text-[9px] tracking-tight text-ink-300">
                                <span>session · </span>{sessionId.replace(/^ivr-build-/, '')}
                            </code>
                        )}
                        {sessionError && (
                            <span className="font-mono text-[9px] uppercase tracking-wider text-danger-500">
                                session failed
                            </span>
                        )}
                    </div>

                    {!readOnly && <div className="hidden sm:block h-9 w-px bg-cream-300" aria-hidden />}

                    {!readOnly && (
                        <button
                            onClick={() => setLineBindOpen(true)}
                            title={t('line.bind.title')}
                            className="group inline-flex items-center gap-2 pl-2.5 pr-4 py-1.5 rounded-md text-white shadow-[0_2px_10px_rgba(6,199,85,0.28)] hover:shadow-[0_4px_18px_rgba(6,199,85,0.4)] hover:-translate-y-0.5 transition-all duration-200"
                            style={{ background: '#06C755' }}
                        >
                            <img src="/line-icon.png" alt="LINE" className="size-7 select-none" draggable={false} />
                            <span className="flex flex-col items-start leading-none gap-1">
                                <span className="font-mono text-[9px] uppercase tracking-[0.22em]" style={{ color: 'rgba(255,255,255,0.72)' }}>LINE</span>
                                <span className="heading-display text-[15px] font-semibold tracking-tight">{t('line.bind.buttonLabel')}</span>
                            </span>
                        </button>
                    )}

                    {!readOnly && (
                        <PublishButton
                            disabled={!sessionId || chat.isStreaming}
                            isStreaming={chat.isStreaming}
                            onClick={handlePublish}
                        />
                    )}
                </div>
            </div>

            {!readOnly && (
                <LineBindModal
                    open={lineBindOpen}
                    onClose={() => setLineBindOpen(false)}
                    avatarId={avatarId}
                    avatarName={avatar?.name}
                />
            )}

            {publishError && (
                <div className="mx-1 -mt-1 px-3 py-1.5 rounded-md bg-danger-50 border border-danger-200 text-danger-700 text-xs flex items-center gap-2">
                    <X size={13} className="shrink-0" />
                    <span>{publishError}</span>
                </div>
            )}

            <div
                className="grid gap-2 flex-1 min-h-0"
                style={{ gridTemplateColumns: '320px minmax(0, 1fr) 360px' }}
            >
                <Card padded={false} className="flex flex-col min-h-0">
                    <PaneHeader icon={FileText} title={t('pane.sources.title')} subtitle={t('pane.sources.subtitle')} />
                    <SourcesPane
                        avatar={avatar}
                        flowMode={avatar?.flowMode}
                        sendMessage={chat.sendMessage}
                        isStreaming={chat.isStreaming}
                        lastCompletedAt={chat.lastCompletedAt}
                        readOnly={readOnly}
                    />
                </Card>

                <Card padded={false} className="flex flex-col min-h-0">
                    <PaneHeader
                        icon={MessageSquare}
                        title={t('pane.chat.title')}
                        subtitle={readOnly ? t('pane.chat.subtitleReadOnly') : t('pane.chat.subtitle')}
                        right={!readOnly && <BuildProgressBadge chat={chat} />}
                    />
                    {readOnly ? (
                        <div className="flex-1 flex flex-col items-center justify-center gap-2 text-ink-400 text-sm px-6 text-center">
                            <Lock size={20} strokeWidth={1.6} />
                            <div>{t('pane.chat.readOnlyTitle')}</div>
                            <div className="text-ink-300 text-xs">{t('pane.chat.readOnlyHint')}</div>
                        </div>
                    ) : sessionId ? (
                        <ChatPane chat={chat} avatarName={avatar?.name} />
                    ) : (
                        <div className="flex-1 flex items-center justify-center text-ink-400 text-sm italic">
                            {t('pane.chat.loadingSession')}
                        </div>
                    )}
                </Card>

                <Card padded={false} className="flex flex-col min-h-0">
                    <PaneHeader icon={Wrench} title={t('pane.workshop.title')} subtitle={t('pane.workshop.subtitle')} />
                    <WorkshopPane
                        readOnly={readOnly}
                        ready={!!avatar}
                        newMode={avatar?.newMode || (avatar?.surveyMode ? 'survey' : null)}
                        flowMode={avatar?.flowMode}
                        sendMessage={chat.sendMessage}
                        isStreaming={chat.isStreaming}
                        avatarId={avatarId}
                        avatarName={avatar?.name}
                        spaceId={avatar?.spaceId || avatar?.raw?.spaceId}
                        hostId={avatar?.raw?.hostId || avatar?.hostId}
                        did={didsByAvatar?.[avatarId]?.[0] || null}
                        testDialerSetup={avatar?.raw?.testDialerSetup || null}
                        currentProvider={avatar?.raw?.ivrFlowConfig?.liveConfig?.provider || 'gemini'}
                        currentVoiceId={
                            // realtime provider（openai / 舊資料 grok）音色存 liveConfig.voice；gemini 存 defaultVoiceId
                            ['openai', 'openai-live', 'grok', 'qwen-omni-tts', 'qwen'].includes((avatar?.raw?.ivrFlowConfig?.liveConfig?.provider || 'gemini').toLowerCase())
                                ? (avatar?.raw?.ivrFlowConfig?.liveConfig?.voice || avatar?.defaultVoiceId)
                                : avatar?.defaultVoiceId
                        }
                        currentFirstMessage={
                            avatar?.raw?.ivrFlowConfig?.greetingMp3?.text
                            || avatar?.raw?.ivrFlowConfig?.routerConfig?.greeting
                            || avatar?.raw?.ivrFlowConfig?.nodes?.find?.(n => n.type === 'router')?.routerConfig?.greeting
                            || avatar?.firstMessage
                            || ''
                        }
                        currentLanguage={
                            avatar?.raw?.ivrFlowConfig?.primaryLanguage
                            || avatar?.raw?.defaultLanguage
                            || avatar?.defaultLanguage
                            || 'zh-TW'
                        }
                        currentMultiLingual={!!avatar?.raw?.ivrFlowConfig?.multiLingual}
                        currentKeyword={avatar?.raw?.keyword || avatar?.raw?.transcriptionKeyword || []}
                        currentVoiceCorrectionWords={avatar?.voiceCorrectionWords || avatar?.raw?.voiceCorrectionWords || []}
                        currentBrandColor={avatar?.brandColor || avatar?.raw?.brandColor || ''}
                        currentOrbLabel={avatar?.orbLabel || avatar?.raw?.orbLabel || ''}
                        currentOrbImageUrl={avatar?.orbImageUrl || avatar?.raw?.orbImageUrl || ''}
                    />
                </Card>
            </div>
        </div>
    );
}

function AvatarNameEditor({ name, onRename }) {
    const { t } = useTranslation('workspace');
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(name);
    const [saving, setSaving] = useState(false);

    const start = () => { setDraft(name); setEditing(true); };
    const cancel = () => { setEditing(false); setDraft(name); };
    const commit = async () => {
        const trimmed = draft.trim();
        if (!trimmed || trimmed === name) { cancel(); return; }
        setSaving(true);
        try {
            await onRename(trimmed);
            setEditing(false);
        } catch (e) {
            alert(t('rename.failed', { error: e.message }));
        } finally {
            setSaving(false);
        }
    };

    if (editing) {
        return (
            <div className="flex items-center gap-1.5">
                <input
                    autoFocus
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') commit();
                        else if (e.key === 'Escape') cancel();
                    }}
                    disabled={saving}
                    className="heading-display text-base font-semibold text-ink-900 bg-white border border-accent-400 rounded px-2 py-0.5 outline-none focus:border-accent-500 min-w-[200px]"
                />
                <button
                    onClick={commit}
                    disabled={saving}
                    title={t('rename.saveTitle')}
                    className="size-7 inline-flex items-center justify-center rounded text-success-500 hover:bg-cream-100 transition"
                >
                    <Check size={14} strokeWidth={2} />
                </button>
                <button
                    onClick={cancel}
                    disabled={saving}
                    title={t('rename.cancelTitle')}
                    className="size-7 inline-flex items-center justify-center rounded text-ink-400 hover:bg-cream-100 transition"
                >
                    <X size={14} strokeWidth={2} />
                </button>
            </div>
        );
    }

    return (
        <div className="group inline-flex items-center gap-1.5">
            <span className="heading-display text-base font-semibold text-ink-900">
                {name}
            </span>
            <button
                onClick={start}
                title={t('rename.editTitle')}
                className="size-6 inline-flex items-center justify-center rounded text-ink-300 hover:text-accent-700 hover:bg-cream-100 transition"
            >
                <Pencil size={12} strokeWidth={1.8} />
            </button>
        </div>
    );
}

function PublishButton({ disabled, isStreaming, onClick }) {
    const { t } = useTranslation('workspace');
    return (
        <button
            onClick={onClick}
            disabled={disabled}
            title={t('publish.tooltip')}
            className={[
                'group relative inline-flex items-center gap-2.5 pl-2.5 pr-4 py-1.5 rounded-md',
                'bg-accent-500 hover:bg-accent-700 active:bg-accent-900',
                'shadow-[0_2px_10px_rgba(200,74,44,0.22)] hover:shadow-[0_4px_18px_rgba(200,74,44,0.32)]',
                'hover:-translate-y-0.5 transition-all duration-200',
                'text-cream-50',
                'disabled:bg-cream-200 disabled:text-ink-400 disabled:shadow-none disabled:hover:translate-y-0 disabled:cursor-not-allowed',
            ].join(' ')}
        >
            <span
                className={[
                    'inline-flex items-center justify-center size-7 rounded',
                    'bg-accent-700/60 group-hover:bg-accent-900/55',
                    'group-disabled:bg-cream-100',
                ].join(' ')}
            >
                <Rocket
                    size={13}
                    strokeWidth={1.8}
                    className={isStreaming ? 'animate-pulse' : ''}
                />
            </span>
            <span className="flex flex-col items-start leading-none gap-1">
                <span
                    className={[
                        'font-mono text-[9px] uppercase tracking-[0.22em]',
                        'text-accent-100 group-disabled:text-ink-300',
                    ].join(' ')}
                >
                    {isStreaming ? 'Syncing' : 'Deploy'}
                </span>
                <span className="heading-display text-[15px] font-semibold tracking-tight">
                    {isStreaming ? t('publish.publishing') : t('publish.publish')}
                </span>
            </span>
        </button>
    );
}

function PaneHeader({ icon: Icon, title, subtitle, right }) {
    return (
        <div className="px-4 py-3 border-b border-cream-200 flex items-center gap-2">
            <Icon size={16} className="text-ink-500" strokeWidth={1.6} />
            <div className="min-w-0">
                <div className="heading-display text-sm font-semibold text-ink-900 leading-tight">{title}</div>
                <div className="font-mono text-[10px] uppercase tracking-wider text-ink-400">{subtitle}</div>
            </div>
            {right && <div className="ml-auto">{right}</div>}
        </div>
    );
}
